import React, { useState, useEffect, useMemo } from "react";
import DesktopSelector from "./DesktopSelector";
import SnapsWise from "./SnapsWise";
import { loadCourse } from "./guidelines/loadCourse"; // adjust path

const AboutCourseModal = ({ open, onClose, subject, course }) => {
  console.log(course, "course")
  const AboutPanel = course?.AboutPanel || null;

  return (
    <div
      className={[
        "fixed inset-0 z-[80]",
        open ? "pointer-events-auto" : "pointer-events-none",
      ].join(" ")}
      aria-hidden={!open}
    >
      <div
        className={[
          "absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-200",
          open ? "opacity-100" : "opacity-0",
        ].join(" ")}
        onClick={onClose}
      />

      <div
        className={[
          "absolute left-1/2 top-1/2 w-[min(94vw,980px)] max-h-[88vh]",
          "-translate-x-1/2 -translate-y-1/2",
          "transition-all duration-200",
          open ? "opacity-100 scale-100" : "opacity-0 scale-[0.98]",
        ].join(" ")}
        role="dialog"
        aria-modal="true"
        aria-label={`About ${subject?.title || "course"}`}
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-white/40 overflow-hidden flex flex-col max-h-[88vh]">
          <div className="px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between">
            <div className="font-semibold">About this course</div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-white/15 hover:bg-white/25 border border-white/25 px-3 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              Close
            </button>
          </div>

          <div className="p-4 overflow-y-auto">
            {AboutPanel ? (
              <AboutPanel />
            ) : (
              <div className="rounded-xl border border-gray-200 bg-slate-50 p-4">
                <div className="font-semibold text-gray-900">
                  {subject?.title || "Course"}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  No course-specific About component found.
                </div>
                <div className="text-xs text-gray-500 mt-3">
                  Add:{" "}
                  <code className="font-mono">
                    courses/{subject?.course_key}/about.js
                  </code>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- helpers ----------
const basename = (p) => String(p || "").split("/").pop() || "";

const canonBase = (b) =>
  String(b || "")
    .replace(/\r/g, "")
    .replace(/\u00A0/g, " ")
    .trim()
    .replace(/dialogue/gi, "dialoge");

const normKey = (s) => canonBase(s).toLowerCase();

const flytExerciseFromFilename = (filename) => {
  const base = canonBase(basename(filename));
  const m = base.match(/^(\d{1,2})\s*([ab])(?=[._-]|$)/i);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (!Number.isFinite(n)) return null;
  return `${n}${m[2].toUpperCase()}`;
};

const Audiobook = ({ subject, exerciseHandler }) => {
  console.log(subject, "subject")
  const [transcripts, setTranscripts] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [currentScript, setCurrentScript] = useState(null);
  const [key, setKey] = useState(0);

  const [course, setCourse] = useState(null);
  const [isAboutOpen, setIsAboutOpen] = useState(false);

  const [shareStatus, setShareStatus] = useState(null); // null | "copied" | "failed"

  const tabLabelMap = useMemo(() => {
    const meta = course?.audioMeta;
    if (!Array.isArray(meta) || meta.length === 0) return null;

    const map = new Map();
    for (const it of meta) {
      const rawBase = basename(it?.file);
      if (!rawBase) continue;

      const title = it?.uiTitle || it?.title;
      if (!title) continue;

      map.set(rawBase, title);
      map.set(normKey(rawBase), title);
      map.set(canonBase(rawBase), title);
    }
    return map;
  }, [course]);

  const tabTitle = useMemo(() => {
    const idx1 = activeTabIndex + 1;

    const currentFileRaw = basename(currentScript?.file);
    const currentFileCanon = canonBase(currentFileRaw);
    const currentFileNorm = normKey(currentFileRaw);

    const metadataTitle =
      (tabLabelMap?.get?.(currentFileRaw) ||
        tabLabelMap?.get?.(currentFileCanon) ||
        tabLabelMap?.get?.(currentFileNorm)) ??
      null;

    if (metadataTitle) return metadataTitle;

    if (subject?.course_key === "flyt-og-rytme") {
      const ex = flytExerciseFromFilename(currentScript?.file);
      return ex ? `Exercise ${ex}` : `Exercise ${idx1}`;
    }

    return `Part ${idx1}`;
  }, [activeTabIndex, currentScript, subject?.course_key, tabLabelMap]);

  function copyShareLink(subjectArg) {
    const courseId =
      subjectArg?.course_id || subjectArg?.id || subjectArg?.course_key;
    if (!courseId) {
      setShareStatus("failed");
      setTimeout(() => setShareStatus(null), 1200);
      return;
    }

    const url = `${window.location.origin}/course/${encodeURIComponent(courseId)}`;

    const doFallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
        setShareStatus("copied");
      } catch {
        setShareStatus("failed");
      } finally {
        setTimeout(() => setShareStatus(null), 1200);
      }
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          setShareStatus("copied");
          setTimeout(() => setShareStatus(null), 1200);
        })
        .catch(doFallback);
    } else {
      doFallback();
    }
  }

  const getTranscripts = async (courseObj) => {
    const token = localStorage.getItem("token");
    const outdata = {
      owner: courseObj.owner,
      author: courseObj.author,
      title: courseObj.title,
      language: courseObj.language,
      type: courseObj.type,
    };

    const response = await fetch(
      `${process.env.REACT_APP_USER_API_URL}/subjects/get-structure`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(outdata),
      }
    );

    if (response.ok) {
      const json = await response.json();
      setTranscripts(json);
    }
  };

  useEffect(() => {
    const ck = subject?.course_key;
    if (!ck) {
      setCourse(null);
      return;
    }
    let alive = true;
    loadCourse(ck)
      .then((c) => {
        if (!alive) return;
        setCourse(c);
      })
      .catch((err) => {
        console.warn("loadCourse failed:", err);
        if (!alive) return;
        setCourse(null);
      });
    return () => {
      alive = false;
    };
  }, [subject?.course_key]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setIsAboutOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (subject) getTranscripts(subject);
  }, [subject]);

  useEffect(() => {
    if (transcripts && transcripts.length > 0) {
      setCurrentScript(transcripts[activeTabIndex]);
      setKey((prev) => prev + 1);
    }
  }, [activeTabIndex, transcripts]);

  useEffect(() => {
    if (transcripts && transcripts.length > 0) {
      const files = transcripts
        .filter((x) => x && "file" in x)
        .map((x) => {
          const f = x.file;
          if (typeof f === "string") return f;
          if (f && typeof f === "object") return f.file || f.full_path || "";
          return "";
        })
        .filter(Boolean);

      setTabs(files);
    }
  }, [transcripts]);

  const hasMultipleParts = tabs && tabs.length > 1;

  const IconButton = ({ onClick, title, children }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="
        inline-flex items-center justify-center
        h-8 w-8
        rounded-lg
        bg-white/10 hover:bg-white/18
        border border-white/20
        text-white
        transition
        focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70
        active:scale-[0.98]
      "
    >
      {children}
    </button>
  );

  const AboutShareIcons = () => {
    if (!subject?.course_key) return null;

    return (
      <div className="flex items-center gap-1.5 flex-shrink-0 relative">
        <IconButton onClick={() => setIsAboutOpen(true)} title="About this course">
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-11.5a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM9.25 8.75a.75.75 0 011.5 0v5a.75.75 0 01-1.5 0v-5z"
              clipRule="evenodd"
            />
          </svg>
        </IconButton>

        <div className="relative">
          <IconButton onClick={() => copyShareLink(subject)} title="Copy share link">
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M15 8a3 3 0 10-2.83-4H12a3 3 0 00.17 1l-5.2 2.6a3 3 0 00-1.97-.6 3 3 0 100 6c.73 0 1.4-.26 1.97-.7l5.2 2.6a3 3 0 10.63-1.2l-5.32-2.66a3.04 3.04 0 000-1.08l5.32-2.66A2.99 2.99 0 0015 8z" />
            </svg>
          </IconButton>

          {shareStatus && (
            <div className="absolute -top-2 -right-2 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-white text-slate-900 shadow border border-white/60">
              {shareStatus === "copied" ? "Copied" : "No link"}
            </div>
          )}
        </div>
      </div>
    );
  };

  return tabs && tabs.length > 0 && currentScript ? (
    // ✅ Stronger bluish background
    <div className="h-screen w-screen overflow-hidden flex flex-col relative bg-gradient-to-br from-slate-100 via-blue-100/70 to-indigo-200/60">
      {/* ✅ Stronger bluish header (less “paper white”) */}
      <div className="w-full flex-shrink-0 border-b border-white/30 relative z-30 bg-gradient-to-r from-blue-600/90 to-indigo-700/90 backdrop-blur-md">
        {/* Desktop header (premium) */}
        <div className="hidden xl:flex items-center justify-between px-3 py-2">
          {/* LEFT: breadcrumb + title */}
         <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              type="button"
              onClick={() => exerciseHandler(null)}
              className="
                inline-flex items-center gap-1.5
                text-white/90 hover:text-white
                text-sm font-semibold
                px-1.5 py-1
                rounded-md
                hover:bg-white/10
                transition
                focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70
                whitespace-nowrap
              "
              title="Back to Library"
              aria-label="Back to Library"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Library</span>
            </button>

            <span className="text-white/40 select-none">/</span>

            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="font-semibold text-sm text-white truncate leading-none min-w-0" title={subject.title}>
                  {subject.title}
                </h1>
                <AboutShareIcons />
              </div>
              <p className="text-xs text-blue-100/85 truncate mt-0.5" title={`by ${subject.author}`}>
                by {subject.author}
              </p>
            </div>
          </div>

          {/* Right: exercise navigation */}
          {hasMultipleParts ? (
            <div className="justify-self-end">
              <DesktopSelector
                clickHandler={(tab_index) => setActiveTabIndex(tab_index)}
                tabsData={tabs}
                active={activeTabIndex}
                courseKey={subject?.course_key}
                tabLabelMap={tabLabelMap}
              />
            </div>
          ) : (
            <div />
          )}
        </div>

        {/* Mobile header (keep your current mobile logic) */}
        <div className="xl:hidden px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <button
                type="button"
                onClick={() => exerciseHandler(null)}
                className="
                  inline-flex items-center gap-1.5
                  text-white/90 hover:text-white
                  text-xs font-semibold
                  px-1.5 py-1
                  rounded-md
                  hover:bg-white/10
                  transition
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70
                  whitespace-nowrap
                  min-h-[44px]
                "
                title="Back to Library"
                aria-label="Back to Library"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Library</span>
              </button>

              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <h1 className="font-semibold text-sm text-white truncate leading-none min-w-0" title={subject.title}>
                    {subject.title}
                  </h1>
                  <AboutShareIcons />
                </div>
                <p className="text-xs text-blue-100/85 truncate mt-0.5" title={`by ${subject.author}`}>
                  by {subject.author}
                </p>
              </div>
            </div>

            {/* keep your mobile prev/next if you like */}
            {hasMultipleParts && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  className="text-white hover:text-blue-100 p-1 hover:bg-white/20 rounded disabled:opacity-40 transition-colors"
                  onClick={() => setActiveTabIndex(Math.max(0, activeTabIndex - 1))}
                  disabled={activeTabIndex === 0}
                  title="Previous"
                  type="button"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <div className="text-white text-xs font-medium px-1.5 min-w-[3rem] text-center">
                  {activeTabIndex + 1}/{tabs.length}
                </div>

                <button
                  className="text-white hover:text-blue-100 p-1 hover:bg-white/20 rounded disabled:opacity-40 transition-colors"
                  onClick={() => setActiveTabIndex(Math.min(tabs.length - 1, activeTabIndex + 1))}
                  disabled={activeTabIndex === tabs.length - 1}
                  title="Next"
                  type="button"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content (give it a bluish wash too) */}
      <div className="flex-1 min-h-0 min-w-0 bg-gradient-to-br from-slate-100 via-blue-100/50 to-indigo-200/40">
        <SnapsWise
          key={`${currentScript?.file}-${key}`}
          subject={subject}
          file={currentScript}
          sectionTitle={tabTitle}
        />
      </div>

      <AboutCourseModal
        open={isAboutOpen}
        onClose={() => setIsAboutOpen(false)}
        subject={subject}
        course={course}
      />
    </div>
  ) : (
    <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-slate-100 via-blue-100/70 to-indigo-200/60">
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/40 shadow-lg">
        <div className="text-gray-700 text-lg">Loading your audiobook...</div>
      </div>
    </div>
  );
};

export default Audiobook;